import Vue from 'vue';
import { APP_MODE, APP_STATE, APP_TYPE } from '@videotools/js/constants/app';
import {
	SET_APP_STATE,
	SET_APP_TYPE,
	SET_APP_MODE,
	SET_COUCH_MODE,
	SET_CINEMA_MODE,
	SET_PLAYER_TYPE,
	SET_STATE,
	SET_TAG_LIST_ACTIVE_TAB,
	SET_CLIP_LIST_ACTIVE_TAB,
	SET_PREVIEW_MODE,
	SET_PREVIEW_DURATION,
	SET_TAGGING_TOOL_SETTINGS_OPEN,
	SET_SHOW_TAG_GROUP_CREATE_PAGE,
	SET_SIDEBAR_OPEN,
} from '@videotools/js/constants/mutations';
import PLAYER_TYPE from '@videotools/js/constants/playerTypes';
import { playerBus } from '@videotools/js/events';

const getInitialState = (state = null) => ({
	appType:                APP_TYPE.TAGGING_TOOL,
	appState:               APP_STATE.INITIALIZING,
	appMode:                APP_MODE.UNMANAGED,
	playerType:             state?.playerType ?? PLAYER_TYPE.CONTEXT,
	isInCouchMode:          false,
	isCinemaMode:           false,
	isPreviewMode:          false,
	isSidebarOpen:          true,
	initialPreviewDuration: null,
	tagList:                { activeTab: null },
	clipList:               { activeTab: null },
	taggingToolSettings:    {
		isOpen:                 false,
		showTagGroupCreatePage: false,
	},
});

export default {
	namespaced: true,
	state:      getInitialState(),
	mutations:  {
		[SET_STATE]: (state, value) => {
			for (const [k, v] of Object.entries(value)) {
				state[k] = v;
			}
		},
		[SET_APP_STATE]: (state, value) => {
			state.appState = value;
		},
		[SET_APP_TYPE]: (state, value) => {
			state.appType = value;
		},
		[SET_APP_MODE]: (state, value) => {
			state.appMode = value;
		},
		[SET_PLAYER_TYPE]: (state, value) => {
			state.playerType = value;
		},
		[SET_COUCH_MODE]: (state, value) => {
			state.isInCouchMode = value;
		},
		[SET_CINEMA_MODE]: (state, value) => {
			state.isCinemaMode = Boolean(value);
		},
		[SET_TAG_LIST_ACTIVE_TAB]: (state, value) => {
			Vue.set(state.tagList, 'activeTab', value);
		},
		[SET_CLIP_LIST_ACTIVE_TAB]: (state, value) => {
			Vue.set(state.clipList, 'activeTab', value);
		},
		[SET_PREVIEW_MODE]: (state, value) => {
			Vue.set(state, 'isPreviewMode', value);
		},
		[SET_PREVIEW_DURATION]: (state, value) => {
			state.initialPreviewDuration = value;
		},
		[SET_TAGGING_TOOL_SETTINGS_OPEN]: (state, value) => {
			Vue.set(state.taggingToolSettings, 'isOpen', value);
		},
		[SET_SHOW_TAG_GROUP_CREATE_PAGE]: (state, value) => {
			Vue.set(state.taggingToolSettings, 'showTagGroupCreatePage', value);
		},
		[SET_SIDEBAR_OPEN]: (state, value) => {
			state.isSidebarOpen = value;
		},
	},
	actions: {
		setState:                                  ({ commit }, value) => commit(SET_STATE, value),
		reset:                                     ({ state, commit }) => commit(SET_STATE, getInitialState(state)),
		setAppState:                               ({ commit }, value) => commit(SET_APP_STATE, value),
		setAppStateNormal:                         ({ commit }) => commit(SET_APP_STATE, APP_STATE.NORMAL),
		setAppStateInitializing:                   ({ commit }) => commit(SET_APP_STATE, APP_STATE.INITIALIZING),
		setAppType:                                ({ commit }, value) => commit(SET_APP_TYPE, value),
		setAppMode:                                ({ commit }, value) => commit(SET_APP_MODE, value),
		setAppTypeTaggingTool:                     ({ commit }) => commit(SET_APP_TYPE, APP_TYPE.TAGGING_TOOL),
		setAppTypeViewMode:                        ({ commit }) => commit(SET_APP_TYPE, APP_TYPE.VIEW_MODE),
		setAppTypeFullPageMode:                    ({ commit }) => commit(SET_APP_TYPE, APP_TYPE.FULL_PAGE_MODE),
		setAppTypeMoments:                         ({ commit }) => commit(SET_APP_TYPE, APP_TYPE.MOMENTS),
		setPlayerType:                             ({ commit }, value) => commit(SET_PLAYER_TYPE, value),
		setCouchMode:                              ({ commit }, value) => commit(SET_COUCH_MODE, value),
		enableCouchMode:                           ({ commit }) => commit(SET_COUCH_MODE, true),
		disableCouchMode:                          ({ commit }) => commit(SET_COUCH_MODE, false),
		setCinemaMode:                             ({ commit }, value) => commit(SET_CINEMA_MODE, value),
		disableCinemaMode:                         ({ dispatch }) => dispatch('setCinemaMode', false),
		enableCinemaMode:                          ({ dispatch }) => dispatch('setCinemaMode', true),
		setSequentialCompetitionTagListActiveTab:  ({ commit }, value) => commit(SET_TAG_LIST_ACTIVE_TAB, value),
		setSequentialCompetitionClipListActiveTab: ({ commit }, value) => commit(SET_CLIP_LIST_ACTIVE_TAB, value),
		setPreviewMode:                            ({ commit, dispatch }, value) => {
			commit(SET_PREVIEW_MODE, true);
			commit(SET_PREVIEW_DURATION, value);
			dispatch('features/setForceDisableChromeCast', true, { root: true });
			dispatch('features/setPlayerSeekEnabled', false, { root: true });
			dispatch('features/setTimelineMinimal', true, { root: true });
		},
		setTaggingToolSettingsOpen: ({ commit }, value) => {
			commit(SET_TAGGING_TOOL_SETTINGS_OPEN, value);
			commit(SET_SHOW_TAG_GROUP_CREATE_PAGE, false);

			if (value) {
				playerBus.pause();
			}
		},
		setShowTagGroupCreatePage: ({ commit }, value) => {
			commit(SET_TAGGING_TOOL_SETTINGS_OPEN, value);
			commit(SET_SHOW_TAG_GROUP_CREATE_PAGE, value);

			if (value) {
				playerBus.pause();
			}
		},
		setSidebarOpen: ({ commit }, value) => {
			commit(SET_SIDEBAR_OPEN, value);
		},
	},
	getters: {
		appType:                                (state) => state.appType,
		appMode:                                (state) => state.appMode,
		isTaggingToolMode:                      (state) => state.appType === APP_TYPE.TAGGING_TOOL,
		isViewMode:                             (state) => state.appType === APP_TYPE.VIEW_MODE,
		isFullPageMode:                         (state) => state.appType === APP_TYPE.FULL_PAGE_MODE,
		isViewModeOrFullPageMode:               (state) => state.appType === APP_TYPE.VIEW_MODE || state.appType === APP_TYPE.FULL_PAGE_MODE,
		isMomentsMode:                          (state) => state.appType === APP_TYPE.MOMENTS,
		isEmbedded:                             (state) => state.appMode === APP_MODE.EMBEDDED,
		isMobileWebApp:                         (state, getters, rootState, rootGetters) => state.appMode !== APP_MODE.EMBEDDED && rootGetters['device/isMobile'],
		isDesktopWebApp:                        (state, getters, rootState, rootGetters) => state.appMode !== APP_MODE.EMBEDDED && rootGetters['device/isDesktop'],
		isMobileWebAppPortrait:                 (state, getters, rootState, rootGetters) => state.appMode !== APP_MODE.EMBEDDED && rootGetters['device/isMobile'] && rootGetters['device/isPortrait'],
		isMobileWebAppLandscape:                (state, getters, rootState, rootGetters) => state.appMode !== APP_MODE.EMBEDDED && rootGetters['device/isMobile'] && rootGetters['device/isLandscape'],
		appState:                               (state) => state.appState,
		isInitialized:                          (state) => state.appState !== APP_STATE.INITIALIZING,
		playerType:                             (state) => state.playerType,
		isInCouchMode:                          (state) => state.isInCouchMode,
		isCinemaMode:                           (state, getters, rootState, rootGetters) => state.isCinemaMode && !rootGetters['device/fullscreen'],
		sequentialCompetitionTagListActiveTab:  (state) => state.tagList.activeTab,
		sequentialCompetitionClipListActiveTab: (state) => state.clipList.activeTab,
		initialPreviewDuration:                 (state) => state.initialPreviewDuration,
		isPreviewMode:                          (state) => state.isPreviewMode,
		isTaggingToolSettingsOpen:              (state) => state.taggingToolSettings.isOpen,
		showTagGroupCreatePage:                 (state) => state.taggingToolSettings.showTagGroupCreatePage,
		isSidebarOpen:                          (state, getters, rootState, rootGetters) => state.isSidebarOpen || rootGetters['device/isNarrowTaggingTool'],
	},
};
